import api from "./api";

export interface UploadResponse {
  success: boolean;
  message: string;
  data?: {
    url: string;
    filename: string;
    size: number;
    type: string;
  };
  error?: string;
}

class UploadService {
  private readonly MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
  private readonly ALLOWED_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    // Microsoft Office documents
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    "application/vnd.ms-excel", // .xls
    "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
    "application/vnd.ms-powerpoint", // .ppt
    "application/msword", // .doc
    // Additional MIME types for Excel files
    "application/octet-stream", // Sometimes detected for .xlsx
    "application/zip", // .xlsx is essentially a zip file
    // Video files
    "video/mp4",
    "video/avi",
    "video/quicktime",
    "video/x-ms-wmv",
  ];

  private readonly R2_ENDPOINT =
    "https://922b235b4d47b2eb86683af73de05fb0.r2.cloudflarestorage.com/course-resources";

  /**
   * Validate file before upload
   */
  private validateFile(file: File): { valid: boolean; error?: string } {
    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size must be less than 50MB. Current size: ${(
          file.size /
          1024 /
          1024
        ).toFixed(2)}MB`,
      };
    }

    // Check file type by MIME type
    if (!this.ALLOWED_TYPES.includes(file.type)) {
      // Also check by file extension for common office documents
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      const allowedExtensions = [
        "pdf",
        "doc",
        "docx",
        "xls",
        "xlsx",
        "ppt",
        "pptx",
        "txt",
        "jpg",
        "jpeg",
        "png",
        "gif",
        "webp",
        "mp4",
        "avi",
        "mov",
        "wmv",
      ];

      if (!allowedExtensions.includes(fileExtension || "")) {
        return {
          valid: false,
          error: `File type not allowed. Allowed types: PDF, Word documents (.doc, .docx), Excel spreadsheets (.xls, .xlsx), PowerPoint presentations (.ppt, .pptx), Images (.jpg, .png, .gif), Videos (.mp4, .avi), and Text files (.txt)`,
        };
      }
    }

    return { valid: true };
  }

  /**
   * Generate unique filename
   */
  private generateFilename(file: File): string {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const extension = file.name.split(".").pop();
    return `${timestamp}_${randomString}.${extension}`;
  }

  /**
   * Upload file to Cloudflare R2
   */
  async uploadFile(
    file: File,
    destination: string = "course-resources"
  ): Promise<UploadResponse> {
    try {
      // Validate file
      const validation = this.validateFile(file);
      if (!validation.valid) {
        return {
          success: false,
          message: validation.error || "File validation failed",
        };
      }

      // Generate unique filename
      const filename = this.generateFilename(file);

      // Create FormData
      const formData = new FormData();
      formData.append("file", file);
      formData.append("filename", filename);
      formData.append("destination", destination);

      // Upload via backend proxy (which will handle R2 upload)
      const response = await api.post<UploadResponse>(
        "/upload/file",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response as unknown as UploadResponse;
    } catch (error: any) {
      console.error("Upload error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Upload failed",
        error: error.message,
      };
    }
  }

  /**
   * Upload image file - alias for uploadFile that returns just the URL
   */
  async uploadImage(
    file: File,
    destination: string = "landingpage"
  ): Promise<string> {
    const response = await this.uploadFile(file, destination);
    if (!response.success || !response.data?.url) {
      throw new Error(response.message || "Image upload failed");
    }
    return response.data.url;
  }

  /**
   * Upload multiple files
   */
  async uploadMultipleFiles(files: File[]): Promise<{
    success: boolean;
    results: UploadResponse[];
    successCount: number;
    failureCount: number;
  }> {
    const results: UploadResponse[] = [];
    let successCount = 0;
    let failureCount = 0;

    for (const file of files) {
      const result = await this.uploadFile(file);
      results.push(result);

      if (result.success) {
        successCount++;
      } else {
        failureCount++;
      }
    }

    return {
      success: successCount > 0,
      results,
      successCount,
      failureCount,
    };
  }

  /**
   * Get file size in human readable format
   */
  getFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  /**
   * Check if file type is allowed
   */
  isFileTypeAllowed(file: File): boolean {
    return this.ALLOWED_TYPES.includes(file.type);
  }

  /**
   * Check if file size is within limit
   */
  isFileSizeAllowed(file: File): boolean {
    return file.size <= this.MAX_FILE_SIZE;
  }

  /**
   * Delete file from Cloudflare R2
   */
  async deleteFile(
    key: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      await api.delete(`/upload/file?key=${encodeURIComponent(key)}`);
      return {
        success: true,
        message: "File deleted successfully",
      };
    } catch (error: any) {
      console.error("Delete error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Delete failed",
      };
    }
  }
}

export const uploadService = new UploadService();
export default uploadService;
